import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import {
  LEARNING_HUB_LANGUAGES,
  LEARNING_HUB_MODULE_PATH,
  LEARNING_HUB_STATUS,
  LearningHubStatus
} from '../../definitions/learning-hub.constants';
import {
  LearningHubArticleMeta,
  LearningHubBreadcrumb,
  LearningHubCategories,
  LearningHubCategory,
  LearningHubIndex,
  LearningHubLanguage,
  LearningHubLanguageOption
} from '../../definitions/learning-hub.models';
import { LearningHubService } from '../../services/learning-hub.service';
import { categoryArticles, categoryChildren, categoryCommands, resolveLanguage } from '../../utils/learning-hub.utils';

interface CategoryState {
  parent: string;
  child: string | null;
  lang: LearningHubLanguage;
}

interface CategoryData {
  state: CategoryState;
  tree: LearningHubCategories;
  index: LearningHubIndex;
}

/**
 * /learning-hub/category/:parent and /learning-hub/category/:parent/:child (?lang=). A top-level section lists
 * its articles and the ones of its children; the children are shown as chips (subsections).
 */
@Component({
  selector: 'app-learning-hub-category',
  templateUrl: './category.component.html',
  styleUrls: ['./category.component.scss']
})
export class CategoryComponent implements OnInit, OnDestroy {
  readonly modulePath = LEARNING_HUB_MODULE_PATH;
  readonly STATUS = LEARNING_HUB_STATUS;

  lang: LearningHubLanguage;
  tree: LearningHubCategories;
  index: LearningHubArticleMeta[] = [];
  parent: LearningHubCategory | null = null;
  child: LearningHubCategory | null = null;
  children: { category: LearningHubCategory; link: string[] }[] = [];
  parentLink: string[] = [];
  articles: LearningHubArticleMeta[] = [];
  breadcrumb: LearningHubBreadcrumb[] = [];
  options: LearningHubLanguageOption[] = [];
  status: LearningHubStatus = LEARNING_HUB_STATUS.LOADING;

  private readonly DESTROY$ = new Subject<void>();

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _translate: TranslateService,
    private readonly _learningHubService: LearningHubService
  ) {}

  get current(): LearningHubCategory | null {
    return this.child || this.parent;
  }

  ngOnInit(): void {
    combineLatest([this._route.paramMap, this._route.queryParamMap])
      .pipe(
        map(([params, query]: [ParamMap, ParamMap]): CategoryState => ({
          parent: params.get('parent'),
          child: params.get('child'),
          lang: resolveLanguage(query.get('lang'), this._translate.currentLang, this._translate.defaultLang)
        })),
        tap((state: CategoryState) => {
          this.lang = state.lang;
          this.status = LEARNING_HUB_STATUS.LOADING;
        }),
        switchMap((state: CategoryState) =>
          combineLatest([this._learningHubService.getCategories(), this._learningHubService.getIndex(state.lang)]).pipe(
            map(([tree, index]: [LearningHubCategories, LearningHubIndex]): CategoryData => ({ state, tree, index })),
            catchError(() => of(null))
          )
        ),
        takeUntil(this.DESTROY$)
      )
      .subscribe((data: CategoryData | null) => {
        if (data) {
          this._show(data);
        } else {
          this.status = LEARNING_HUB_STATUS.ERROR;
        }
      });
  }

  ngOnDestroy(): void {
    this.DESTROY$.next();
    this.DESTROY$.complete();
  }

  changeLanguage(option: LearningHubLanguageOption): void {
    this._router.navigate([], { relativeTo: this._route, queryParams: { lang: option.lang } });
  }

  private _show({ state, tree, index }: CategoryData): void {
    const find = (key: string): LearningHubCategory | undefined =>
      tree.categories.find((category: LearningHubCategory) => category.key === key);
    const parent = find(state.parent);
    const child = state.child ? find(state.child) : null;
    this.tree = tree;
    this.index = index.articles;
    if (!parent || parent.parent || (state.child && (!child || child.parent !== parent.key))) {
      this.parent = this.child = null;
      this.status = LEARNING_HUB_STATUS.NOT_FOUND;
      this.breadcrumb = [this._homeCrumb()];
      this.options = LEARNING_HUB_LANGUAGES.map((lang: LearningHubLanguage) => ({ lang, available: true }));
      return;
    }
    this.parent = parent;
    this.child = child;
    this.parentLink = categoryCommands(parent.key, tree.categories);
    this.children = categoryChildren(parent, tree.categories, state.lang).map((category: LearningHubCategory) => ({
      category,
      link: categoryCommands(category.key, tree.categories)
    }));
    this.articles = categoryArticles(child || parent, index.articles, state.lang);
    const query = { lang: state.lang };
    this.breadcrumb = [
      this._homeCrumb(),
      { label: `learningHub.categories.${parent.key}`, translate: true, link: this.parentLink, queryParams: query },
      ...(child ? [{ label: `learningHub.categories.${child.key}`, translate: true }] : [])
    ];
    // A category exists in a language when it has articles in it (e.g. "Piezas Standard" is Spanish only).
    this.options = LEARNING_HUB_LANGUAGES.map((lang: LearningHubLanguage) => ({
      lang,
      available: ((child || parent).articles[lang] || []).length > 0
    }));
    this.status = LEARNING_HUB_STATUS.READY;
  }

  private _homeCrumb(): LearningHubBreadcrumb {
    return { label: 'learningHub.title', translate: true, link: ['/', this.modulePath], queryParams: { lang: this.lang } };
  }
}
